import { ReactNode } from "react";
import { Box, Divider, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import {
  BlockButton,
  EjsLinkButtonButton,
  EjsPlaceholderButton,
  Heading1Icon,
  Heading2Icon,
  ImageButton,
  LinkButton,
  MarkButton,
} from "./ToolbarButtons";

/**
 * A run of related controls. Grouping them and separating the groups
 * makes a dozen icons scannable instead of an undifferentiated row.
 */
const Group = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
    {children}
  </Box>
);

const GroupDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
);

/**
 * Editor toolbar.
 *
 * Returns a laid-out bar rather than a bare fragment of buttons: it
 * used to return a fragment, and the consumer rendered it inside a
 * column flex container, so every button stretched to the full width
 * of the editor and the toolbar ran down the page as a dozen rows.
 */
export const Toolbar = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 0.25,
      px: 1,
      py: 0.75,
      border: (theme) => `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      backgroundColor: "background.subtle",
      // The buttons are ToggleButtons; strip their individual borders so
      // the bar reads as one surface rather than a grid of boxes.
      "& .MuiToggleButton-root": {
        border: "none",
        borderRadius: 1.5,
        p: 0.75,
        color: "text.secondary",
        "&:hover": { backgroundColor: "surface.hover" },
        "&.Mui-selected": {
          backgroundColor: "surface.selected",
          color: "text.primary",
          "&:hover": { backgroundColor: "surface.selected" },
        },
        "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
      },
    }}
  >
    <Group>
      <MarkButton markType="bold" value="bold">
        <Tooltip title="Fett">
          <FormatBoldIcon />
        </Tooltip>
      </MarkButton>
      <MarkButton markType="italic" value="italic">
        <Tooltip title="Kursiv">
          <FormatItalicIcon />
        </Tooltip>
      </MarkButton>
      <MarkButton markType="underline" value="underline">
        <Tooltip title="Unterstrichen">
          <FormatUnderlinedIcon />
        </Tooltip>
      </MarkButton>
      <MarkButton markType="strikethrough" value="strikethrough">
        <Tooltip title="Durchgestrichen">
          <StrikethroughSIcon />
        </Tooltip>
      </MarkButton>
    </Group>

    <GroupDivider />

    <Group>
      <BlockButton blockType="heading5" value="heading5">
        <Tooltip title="Mittlere Überschrift">
          <Heading1Icon />
        </Tooltip>
      </BlockButton>
      <BlockButton blockType="heading6" value="heading6">
        <Tooltip title="Kleine Überschrift">
          <Heading2Icon />
        </Tooltip>
      </BlockButton>
      <BlockButton blockType="bulleted-list" value="bulleted-list">
        <Tooltip title="Liste">
          <FormatListBulletedIcon />
        </Tooltip>
      </BlockButton>
      <BlockButton blockType="numbered-list" value="numbered-list">
        <Tooltip title="Nummerierte Liste">
          <FormatListNumberedIcon />
        </Tooltip>
      </BlockButton>
    </Group>

    <GroupDivider />

    <Group>
      <Tooltip title="Link einfügen">
        <LinkButton value="link" />
      </Tooltip>
      <Tooltip title="Bild einfügen">
        <ImageButton value="image" />
      </Tooltip>
    </Group>

    <GroupDivider />

    <Group>
      <Tooltip title="Platzhalter einfügen">
        <EjsPlaceholderButton />
      </Tooltip>
      <Tooltip title="Button-Link einfügen">
        <EjsLinkButtonButton />
      </Tooltip>
    </Group>
  </Box>
);
